import React, { useEffect, useState } from "react";
import { Gauge, Loader2, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/admin/ui";

const secs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const tone = (ms: number) => (ms >= 2000 ? "text-[#B91C1C]" : ms >= 800 ? "text-[#C2410C]" : "text-[#15803D]");

export function PageSpeed() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    api.get(`/admin/performance?days=${days}`).then((r) => { setData(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = (data?.rows || []).filter((r: any) => r.count >= 3).slice(0, 25);
  return (
    <Card title="Page speed" testId="page-speed-card" actions={
      <div className="flex items-center gap-2">
        <select className="field-input h-9 w-auto text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="page-speed-days">
          <option value={1}>Last 24 hours</option><option value={7}>Last 7 days</option><option value={14}>Last 14 days</option>
        </select>
        <button className="btn-secondary btn-sm" onClick={load} disabled={busy} aria-label="Refresh" data-testid="page-speed-refresh">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</button>
      </div>
    }>
      <div className="space-y-3 p-5">
        <p className="text-xs text-[#64748B]">How long the server takes to answer each part of the site, slowest first. "Usual worst" is how slow it is on a bad load (95% of loads are faster). Green is under 0.8s, orange under 2s, red 2s or more. Records are kept for 14 days.</p>
        {err && <p className="text-sm text-[#B91C1C]" data-testid="page-speed-error">{err}</p>}
        {data && !rows.length && <p className="flex items-center gap-2 text-sm text-[#64748B]" data-testid="page-speed-empty"><Gauge className="h-4 w-4" /> Not enough visits recorded yet. Check back after the site has been used for a while.</p>}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm" data-testid="page-speed-table">
              <thead><tr className="text-left text-xs text-[#64748B]"><th className="py-2">Page / area</th><th>Action</th><th className="text-right">Loads</th><th className="text-right">Average</th><th className="text-right">Usual worst</th><th className="text-right">Over 2s</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r: any, i: number) => (
                  <tr key={`${r.method}${r.path}`} data-testid={`page-speed-row-${i}`}>
                    <td className="py-2 font-medium text-[#1A2536]">{r.area}</td>
                    <td className="max-w-[16rem] truncate font-mono text-xs text-[#64748B]" title={`${r.method} ${r.path}`}>{r.method === "GET" ? "Load" : "Save"} {r.path.replace(/^\/api/, "")}</td>
                    <td className="text-right">{r.count}</td>
                    <td className={`text-right font-mono ${tone(r.avg_ms)}`}>{secs(r.avg_ms)}</td>
                    <td className={`text-right font-mono font-semibold ${tone(r.p95_ms)}`}>{secs(r.p95_ms)}</td>
                    <td className="text-right">{r.slow_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
